<template>
    <div>
        <div class="flex justify-between items-center mb-4">
            <h4 class="text-base font-medium leading-6 text-slate-800 dark:text-slate-100">
                Product Changes
            </h4>
            <button
                v-if="
                    activeAccount.product_changes &&
                    activeAccount.product_changes.length > 0
                "
                @click="$emit('add-product-change')"
                class="btn-base btn-transparent text-slate-800 dark:text-slate-100 !-mr-2"
            >
                + Add Another
            </button>
        </div>

        <div
            v-if="
                !activeAccount.product_changes ||
                activeAccount.product_changes.length === 0
            "
            class="flex flex-col items-start p-4 pb-4 rounded-xl bg-slate-50 dark:bg-slate-700/50 mb-6"
        >
            <p class="text-sm text-slate-500 dark:text-slate-400">No product changes recorded</p>
        </div>

        <div v-else>
            <template
                v-for="(productChange, index) in sortedProductChanges"
                :key="productChange.id"
            >
                <div
                    class="flex flex-col items-start p-4 pb-4 rounded-xl bg-slate-50 dark:bg-slate-700/50 relative mb-4"
                >
                    <!-- Card Details Section Header with actions -->
                    <div class="flex justify-between items-center w-full mb-4">
                        <h5
                            class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100"
                        >
                            {{
                                index === sortedProductChanges.length - 1
                                    ? 'Original card details'
                                    : 'Previous card details'
                            }}
                        </h5>
                        <div class="flex gap-1">
                            <button
                                @click="
                                    $emit('edit-product-change', productChange)
                                "
                                class="p-1 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 rounded"
                            >
                                <Icon icon="pencil" :width="14" :height="14" />
                            </button>
                            <button
                                @click="
                                    openDeleteConfirmation(productChange.id)
                                "
                                class="p-1 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 rounded"
                            >
                                <Icon icon="trash" :width="14" :height="14" />
                            </button>
                        </div>
                    </div>

                    <!-- Card Info -->
                    <div class="flex items-center gap-6 w-full mb-4">
                        <div class="flex items-center flex-1">
                            <span
                                class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400"
                            >
                                {{
                                    index === sortedProductChanges.length - 1
                                        ? 'Original card'
                                        : 'Previous card'
                                }}
                            </span>
                        </div>
                        <div class="flex flex-col items-start gap-2 flex-1">
                            <div class="w-auto h-5">
                                <img
                                    v-if="
                                        productChange.previous_card &&
                                        productChange.previous_card.card_image
                                    "
                                    :src="`${cdnUrl}/${productChange.previous_card.card_image?.path}/_small/${productChange.previous_card.card_image?.file_name}`"
                                    class="h-5 w-auto object-contain rounded-sm"
                                    alt="Original card image"
                                />
                                <Icon
                                    v-else
                                    icon="credit-card"
                                    :width="32"
                                    :height="20"
                                />
                            </div>
                            <span
                                class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100"
                            >
                                {{
                                    productChange.previous_card
                                        ? productChange.previous_card.title
                                        : '(Card not found)'
                                }}
                            </span>
                        </div>
                    </div>

                    <!-- Date Opened - Only show for the first (oldest) product change -->
                    <div
                        v-if="index === sortedProductChanges.length - 1"
                        class="flex items-center gap-6 w-full mb-4"
                    >
                        <div class="flex items-center flex-1">
                            <span
                                class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400"
                            >
                                Date opened
                            </span>
                        </div>
                        <div class="flex flex-1">
                            <span
                                class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100"
                            >
                                {{
                                    productChange.product_changed_date_opened
                                        ? formatDate(
                                              productChange.product_changed_date_opened
                                          )
                                        : '(Add original open date)'
                                }}
                            </span>
                        </div>
                    </div>

                    <!-- Date Product Change -->
                    <div class="flex items-center gap-6 w-full">
                        <div class="flex items-center flex-1">
                            <span
                                class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400"
                            >
                                Date product changed
                            </span>
                        </div>
                        <div class="flex flex-1">
                            <span
                                class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100"
                            >
                                {{
                                    productChange.product_changed_date
                                        ? formatDate(
                                              productChange.product_changed_date
                                          )
                                        : '(Add product change date)'
                                }}
                            </span>
                        </div>
                    </div>

                    <!-- Original Welcome Bonus - Only show for the oldest (original) product change if bonus exists -->
                    <div
                        v-if="
                            index === sortedProductChanges.length - 1 &&
                            hasWelcomeBonus
                        "
                        class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 w-full"
                    >
                        <h5
                            class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 mb-3"
                        >
                            Original welcome bonus
                        </h5>
                        <BonusCard
                            :bonus="activeAccount.welcome_bonus"
                            bonusType="welcome_bonus"
                            :bonusTiers="activeAccount.welcome_bonus?.tiers"
                        />
                    </div>
                </div>
            </template>
        </div>

        <Teleport to="body">
            <ConfirmationDialog
                :show="showDeleteConfirmation"
                title="Delete product change"
                dialog="Are you sure you want to delete this product change? This action cannot be undone."
                icon="trash"
                iconColor="red"
                @cancel="cancelDelete"
                @confirm="confirmDelete"
                :buttons="[
                    { type: 'cancel', text: 'Cancel' },
                    {
                        type: 'confirm',
                        text: 'Delete',
                        value: true,
                        classes: '!bg-red-500 !text-white hover:!bg-red-600',
                    },
                ]"
            />
        </Teleport>
    </div>
</template>
<script setup>
import { computed, ref } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import ConfirmationDialog from '@/components/ui/confirmation-dialog/confirmation-dialog.vue';
import BonusCard from '@/components/wallet/partials/bonus-card.vue';

const props = defineProps({
    activeAccount: {
        type: Object,
        required: true,
    },
});

const emit = defineEmits([
    'add-product-change',
    'edit-product-change',
    'delete-product-change',
]);

const showDeleteConfirmation = ref(false);
const pendingDeleteId = ref(null);

const openDeleteConfirmation = (id) => {
    pendingDeleteId.value = id;
    showDeleteConfirmation.value = true;
};

const cancelDelete = () => {
    showDeleteConfirmation.value = false;
    pendingDeleteId.value = null;
};

const confirmDelete = () => {
    if (pendingDeleteId.value !== null) {
        emit('delete-product-change', pendingDeleteId.value);
    }
    showDeleteConfirmation.value = false;
    pendingDeleteId.value = null;
};

const sortedProductChanges = computed(() => {
    if (!props.activeAccount.product_changes) return [];
    return [...props.activeAccount.product_changes].sort((a, b) => {
        const dateA = a.product_changed_date
            ? new Date(a.product_changed_date)
            : null;
        const dateB = b.product_changed_date
            ? new Date(b.product_changed_date)
            : null;
        const timeA = dateA ? dateA.getTime() : null;
        const timeB = dateB ? dateB.getTime() : null;
        const validA = timeA !== null && !Number.isNaN(timeA);
        const validB = timeB !== null && !Number.isNaN(timeB);
        if (!validA && !validB) return 0;
        if (!validA) return 1;
        if (!validB) return -1;
        return timeB - timeA;
    });
});

const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const hasWelcomeBonus = computed(() => {
    const wb = props.activeAccount.welcome_bonus;
    if (!wb) return false;
    // Check if welcome bonus has meaningful data (id or bonus_amount)
    return !!(wb.id || wb.bonus_amount);
});

const cdnUrl = window.cdnUrl;
</script>
